import React from "react"
import ReactTooltip from "react-tooltip"
import numeral from "numeral"
import {camelCase} from "lodash"
import BodyPortal from "../BodyPortal/BodyPortal"
import classNames from "classnames"
import Icon from "../Icon/Icon"

type OverrideInfo = {
	endDate?: string
	depositProductLimit: number
}

type Props = {
	title?: string
	description?: string
	rangeColor?: string
	className?: string
	max: number
	current: number
	softLimit?: number
	onEdit?: () => void
	overrideInfo?: OverrideInfo
}

function selectRangeBoxTrackColor(max: number, current: number, soft = max): string {
	if (soft != max && current > soft && current / max < 0.85) return "account-limits-reach-soft-limit"
	if (current / max > 0.85) return "account-limits-reach-hard-limit"
	else return "account-limits-no-limit"
}

function SoftLimitView({soft, max, id}: {soft: number; max: number; id: string}) {
	return (
		<div
			className="range-box-range-container"
			style={{left: `${(soft * 100) / max}%`}}
			data-tip={true}
			data-iscapture={true}
			data-for={id}
		>
			<div className="range-box-range-determination" />
			<div className="range-box-range-picker" />
			<BodyPortal>
				<ReactTooltip
					arrowColor="#FFF"
					delayHide={100}
					className="range-box-range-container-tooltip"
					place="bottom"
					type="light"
					effect="solid"
					id={id}
				>
					<>
						<span className="range-box-range-container-tooltip-title"> Soft limit: &nbsp; </span>
						<span className="range-box-range-container-tooltip-number"> {numeral(soft).format("$0,0")} </span>
						<p>
							{" "}
							Transactions over your <br /> soft limit will go to manual review
						</p>
						<a href="https://guides.unit.co/limits/" rel="noreferrer" target="_blank">
							{" "}
							Learn more{" "}
						</a>
					</>
				</ReactTooltip>
			</BodyPortal>
		</div>
	)
}

function Range({
	max,
	current,
	soft = max,
	id,
	defaultColor,
}: {
	max: number
	current: number
	soft: number
	id: string
	defaultColor: string
}) {
	return (
		<div className="range-box-range" style={{backgroundColor: defaultColor}}>
			{soft != max ? <SoftLimitView soft={soft} max={max} id={id} /> : ""}

			<div
				className={classNames("range-box-range-rail", selectRangeBoxTrackColor(max, current, soft))}
				style={{width: `${(current / max) * 100}%`}}
			></div>
		</div>
	)
}

function formatOverrideDate(dateString: string): string {
	const datePart = dateString.split("T")[0]
	const [year, month, day] = datePart.split("-").map(Number)
	const date = new Date(year, month - 1, day)
	return date.toLocaleDateString("en-US", {month: "numeric", day: "numeric", year: "numeric"})
}

export default function RangeBox({
	title = "",
	description = "",
	rangeColor = "#E2E4E7",
	className = "",
	max,
	current,
	softLimit = max,
	onEdit,
	overrideInfo,
}: Props): JSX.Element {
	const boxId = camelCase(`${title} ${description}`)
	const overrideTooltipId = `${boxId}-override-tooltip`

	return (
		<div className={classNames("range-box", className)}>
			<div className="range-box-header">
				<div className="range-box-header-text">
					<span className="range-box-header-title">{title}</span>
					<span className="range-box-header-description"> {description} </span>
				</div>
				{onEdit && (
					<button className="range-box-edit-button" onClick={onEdit} type="button" aria-label="Edit limit">
						<Icon icon="pencil-1--interface-essential" size={16} />
					</button>
				)}
			</div>

			<Range max={max / 100} current={current / 100} soft={softLimit / 100} id={boxId} defaultColor={rangeColor} />

			<div className="range-box-range-description">
				<span className="range-box-range-description-current">{numeral(current / 100).format("$0,0.00")}</span>/
				<span className="range-box-range-description-max">{numeral(max / 100).format("$0,0.00")}</span>
			</div>

			{overrideInfo && (
				<div className="range-box-override-indicator">
					<span className="range-box-override-dot" />
					<span className="range-box-override-text">
						{overrideInfo.endDate
							? `You have active limit override until ${formatOverrideDate(overrideInfo.endDate)}`
							: "You have active limit override"}
					</span>
					<span className="range-box-override-info-icon" data-tip={true} data-for={overrideTooltipId}>
						<Icon icon="information-circle--interface-essential" size={16} />
					</span>
					<BodyPortal>
						<ReactTooltip
							arrowColor="#000"
							delayHide={100}
							className="range-box-override-tooltip"
							place="bottom"
							type="dark"
							effect="solid"
							id={overrideTooltipId}
						>
							Default Deposit Product limit: {numeral(overrideInfo.depositProductLimit / 100).format("$0,0.00")}
						</ReactTooltip>
					</BodyPortal>
				</div>
			)}
		</div>
	)
}
